import React from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  FileText,
  Users,
  Clock,
  Link2,
  FileCheck,
  Download,
  GraduationCap,
  Mail,
} from "lucide-react";
import resumen from "../img/seminario/resumen.doc";

export default function CallForPapers() {
  // Variantes para animaciones de Framer Motion
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const dates = [
    {
      date: "16 de Marzo, 2026",
      label: "Publicación de la convocatoria",
      active: false,
    },
    {
      date: "19 de Julio, 2026",
      label: "Fecha límite para el envío de resúmenes",
      active: true,
    },
    {
      date: "31 de Agosto, 2026",
      label: "Notificación de ponencias aceptadas",
      active: false,
    },
    {
      date: "19 de Noviembre, 2026",
      label: "Envío de presentaciones de soporte",
      active: false,
    }
  ];

  return (
    <div className="bg-slate-100 text-slate-800 py-16 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Encabezado Principal */}
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <span className="text-sm font-semibold tracking-wider text-blue-600 uppercase">
            Convocatoria abierta
          </span>
          <h2 className="mt-2 text-4xl font-extrabold text-slate-900 tracking-tight sm:text-5xl">
            Call for papers
          </h2>

          <div className="mt-4 w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
        </motion.div>

        {/* Sección Dirigido a & Dinámica */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div
            variants={fadeIn}
            className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
          >
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl w-fit mb-6">
              <Users size={28} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              ¿A quién va dirigido?
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Investigadores, profesionales, gestores y creadores interesados en
              compartir avances, resultados o reflexiones críticas en torno a
              los ejes temáticos del Seminario.
            </p>
          </motion.div>

          <motion.div
            variants={fadeIn}
            className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
          >
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl w-fit mb-6">
              <Clock size={28} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Dinámica de las Mesas
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Cada ponencia deberá presentar una problemática delimitada basada
              en investigación empírica, reflexión teórica o análisis de
              políticas culturales.
            </p>
            <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg border-l-4 border-indigo-500">
              <strong>Estructura:</strong> 15 minutos de exposición individuales
              en mesas colectivas interdisciplinarias, seguidos de un espacio de
              debate con el público.
            </p>
          </motion.div>
        </motion.div>

        {/* Modalidades de Inscripción y Envío */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Inscripción y envío de propuestas
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Opción Resumen / Ponencia */}
            <motion.div
              className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col justify-between"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full uppercase">
                    General
                  </span>
                  <FileText className="text-blue-600" size={32} />
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">
                  Resumen de ponencia
                </h4>
                <p className="text-slate-600 mb-6">
                  Orientado a contribuir a la comprensión de los procesos
                  contemporáneos en la economía de la cultura.
                </p>

                <ul className="space-y-3 text-sm text-slate-600 mb-6">
                  <li className="flex items-start gap-2 text-slate-600">
                    <span>
                      <strong>Extensión:</strong> Máximo 600 palabras.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <span>
                      <strong>Estructura obligatoria:</strong> Objetivo,
                      metodología y resultados principales (obtenidos o
                      esperados). Debe incluir tema y eje temático.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <span>
                      <strong>Idiomas aceptados:</strong> Español, portugués e
                      inglés (Formato Word).
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                <a
                target="_blank"
                  href="https://docs.google.com/forms/d/e/1FAIpQLSe2q0pNoVTUY09wdJTbcOcZ_Q6Ksl-IkacOM7IUmXme0bDfKA/viewform?usp=header"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors text-sm"
                >
                  <Link2 size={16} /> Enviar Resumen
                </a>
                <a
                  href={resumen}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-slate-100 text-slate-700 font-medium rounded-xl border border-slate-300 transition-colors text-sm"
                >
                  <Download size={16} /> Formato Base
                </a>
              </div>
            </motion.div>

            {/* Opción Póster */}
            <motion.div
              className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col justify-between"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full uppercase flex items-center gap-1">
                    <GraduationCap size={14} /> Exclusivo Estudiantes
                  </span>
                  <FileCheck className="text-amber-600" size={32} />
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">
                  Póster académico
                </h4>
                <p className="text-slate-600 mb-6">
                  Modalidad alternativa permitida{" "}
                  <strong>
                    únicamente para estudiantes de grado y posgrado
                  </strong>{" "}
                  para presentar sus investigaciones.
                </p>

                <ul className="space-y-3 text-sm text-slate-600 mb-6">
                  <li className="flex items-start gap-2 text-slate-600">
                    <span>
                      <strong>Estructura:</strong> Título, Objetivo,
                      Introducción, Datos, Metodología, Resultados y Discusión,
                      Conclusiones, Recomendaciones y Bibliografía.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-600">
                    <span>
                      <strong>Dimensiones físicas:</strong> Tamaño de impresión
                      final A0 (84 cm de ancho × 118 cm de alto).
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                <a
                target="_blank"
                  href="https://docs.google.com/forms/d/e/1FAIpQLSe2q0pNoVTUY09wdJTbcOcZ_Q6Ksl-IkacOM7IUmXme0bDfKA/viewform?usp=header"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl transition-colors text-sm"
                >
                  <Link2 size={16} /> Enviar Póster
                </a>
                <a
                target="_blank"
                  href="https://utpl-my.sharepoint.com/:p:/g/personal/siec2026_utpl_edu_ec/IQDChEgGRTgsQYigbO5Z3a0xAYqaUe-41mWNocCPQ-356C4?e=vk6vMg"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-slate-100 text-slate-700 font-medium rounded-xl border border-slate-300 transition-colors text-sm"
                >
                  <Download size={16} /> Formato Referencia
                </a>
              </div>
            </motion.div>
          </div>

          {/* Nota de Publicación y Soporte */}
          <div className="mt-8 p-6 bg-blue-50/50 rounded-2xl border border-blue-100 text-sm text-slate-700 space-y-3">
            <p className="text-slate-600">
              📢 <strong>Publicación:</strong> Los resúmenes aprobados serán
              publicados en un <strong>Libro de Resúmenes con e-ISSN</strong>,
              previo consentimiento formal de los autores.
            </p>
            <p className="flex flex-wrap items-center gap-1 text-slate-600">
              💻 <strong>Material de apoyo:</strong> Una vez aceptada la
              ponencia, se solicita enviar la presentación de PowerPoint como
              soporte con al menos una semana de anticipación al correo:
              <a
                href="mailto:siec2026@utpl.edu.ec"
                className="inline-flex items-center gap-1 text-blue-600 font-semibold hover:underline ml-1"
              >
                <Mail size={14} /> siec2026@utpl.edu.ec
              </a>
            </p>
          </div>
        </div>

        {/* Línea de Tiempo (Fechas Importantes) */}
        <motion.div
          className="bg-[#004270] text-white p-8 md:p-12 rounded-3xl shadow-xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <div className="flex items-center gap-3 mb-10">
            <Calendar className="text-blue-400" size={28} />
            <h3 className="text-2xl font-bold tracking-tight">
              Fechas Importantes
            </h3>
          </div>

          <div className="relative border-l-2 border-slate-700 ml-3 md:ml-6 space-y-8">
            {dates.map((item, index) => (
              <div key={index} className="relative pl-6 md:pl-8">
                {/* Nodo de la línea de tiempo */}
                <span
                  className={`absolute -left-[11px] top-1.5 w-5 h-5 rounded-full border-4 border-white transition-colors ${
                    item.active ? "bg-amber-400 scale-110" : "bg-slate-500"
                  }`}
                />

                <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
                  <span
                    className={`text-sm font-mono font-bold tracking-wider uppercase ${
                      item.active ? "text-amber-400" : "text-blue-400"
                    }`}
                  >
                    {item.date}
                  </span>
                  <h4 className="text-base font-medium text-slate-200">
                    {item.label}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
